import { type QuizQuoteFormValues } from './quizQuote.schema';
import { type QuizQuotePricing } from './quotePricing';

export type QuizQuoteApiPlanType = 'individual' | 'group';
export type QuizQuoteApiPlan = 'basic' | 'pro';
export const QUOTE_UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export type QuoteUtmParams = Partial<
  Record<(typeof QUOTE_UTM_KEYS)[number], string>
>;

export interface QuizQuoteApiRequest {
  schoolName: string;
  userCount: number;
  term: number;
  startDate: string;
  plan: QuizQuoteApiPlan;
  planType: QuizQuoteApiPlanType;
  accountName: string;
  accountPhone: string;
  accountEmail: string;
  annotation?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  quotedAmount?: number;
  quotedRegularAmount?: number;
  quotedTotalAmount?: number;
  quotedDiscountAmount?: number;
  quotedFinalAmount?: number;
}

export interface QuizQuoteApiErrorBody {
  error?: {
    code?: string;
    message?: string;
    fields?: Partial<Record<keyof QuizQuoteApiRequest, string>>;
    raw?: unknown;
  };
}

export class QuizQuoteApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: QuizQuoteApiErrorBody,
  ) {
    super(body.error?.message || '견적서 발행에 실패했습니다.');
    this.name = 'QuizQuoteApiError';
  }
}

async function parseResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch (_error) {
    return {
      error: {
        code: 'invalid_json_response',
        message: '견적서 발행 서버 응답을 해석할 수 없습니다.',
        raw: text,
      },
    };
  }
}

export function toQuizQuoteApiRequest(
  values: QuizQuoteFormValues,
  utm: QuoteUtmParams = {},
  pricing?: QuizQuotePricing,
): QuizQuoteApiRequest {
  const request: QuizQuoteApiRequest = {
    schoolName: values.school_name,
    accountName: values.account_name,
    accountPhone: values.account_phone,
    accountEmail: values.account_email,
    plan: values.plan,
    planType: values.plan_type,
    userCount: values.user_count,
    term: values.term,
    startDate: values.start_date,
    annotation: values.annotation || undefined,
    utmSource: utm.utm_source,
    utmMedium: utm.utm_medium,
    utmCampaign: utm.utm_campaign,
    utmContent: utm.utm_content,
    utmTerm: utm.utm_term,
  };

  if (pricing) {
    request.quotedAmount = pricing.amount;
    request.quotedRegularAmount = pricing.regularAmount;
    request.quotedTotalAmount = pricing.totalAmount;
    request.quotedDiscountAmount = pricing.discountAmount;
    request.quotedFinalAmount = pricing.finalAmount;
  }

  return request;
}

export async function sendQuizQuoteNotification(
  request: QuizQuoteApiRequest,
): Promise<void> {
  const response = await fetch('/api/pricing/quote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  const body = await parseResponseBody(response);

  if (!response.ok) {
    throw new QuizQuoteApiError(response.status, body as QuizQuoteApiErrorBody);
  }
}
